// src/components/JobChat.js
import React, { useState, useEffect, useRef } from 'react';
import { sendMessage, listenToMessages, markMessagesRead } from '../services/firestoreService';
import './JobChat.css';

const formatTime = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const JobChat = ({ jobId, currentUser, otherUserId }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [sending, setSending]   = useState(false);
  const bottomRef               = useRef(null);
  // Keep a ref to the latest text value so handleSend never reads stale closure state
  const textRef = useRef(text);
  useEffect(() => { textRef.current = text; }, [text]);

  // Diagnostic: log props on mount and whenever they change
  useEffect(() => {
    console.log('[JobChat] mounted/updated — jobId:', jobId, '| currentUser:', currentUser?.uid, '| otherUserId:', otherUserId);
    if (!jobId)       console.warn('[JobChat] ⚠️ jobId is missing — messages will not load');
    if (!otherUserId) console.warn('[JobChat] ⚠️ otherUserId is missing — Send will be blocked');
    if (!currentUser?.uid) console.warn('[JobChat] ⚠️ currentUser.uid is missing');
  }, [jobId, currentUser?.uid, otherUserId]);

  useEffect(() => {
    if (!jobId) return;
    if (!currentUser?.uid) return undefined;
    const unsub = listenToMessages(jobId, currentUser.uid, (msgs) => {
      setMessages(msgs);
      markMessagesRead(jobId, currentUser.uid);
    });
    return unsub;
  }, [jobId, currentUser?.uid]);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const currentText = textRef.current.trim();
    console.log('[JobChat] Send clicked — text:', JSON.stringify(currentText), '| jobId:', jobId, '| otherUserId:', otherUserId, '| uid:', currentUser?.uid);

    if (!currentText) {
      console.warn('[JobChat] blocked: text is empty');
      return;
    }
    if (!jobId) {
      console.warn('[JobChat] blocked: jobId is missing');
      return;
    }
    if (!otherUserId) {
      console.warn('[JobChat] blocked: otherUserId is missing');
      return;
    }
    if (!currentUser?.uid) {
      console.warn('[JobChat] blocked: currentUser.uid is missing');
      return;
    }

    setSending(true);
    try {
      console.log('[JobChat] calling sendMessage...');
      await sendMessage(
        jobId,
        currentUser.uid,
        currentUser.name || 'User',
        currentUser.type || 'customer',
        otherUserId,
        currentText
      );
      console.log('[JobChat] sendMessage succeeded');
      setText('');
    } catch (err) {
      console.error('[JobChat] sendMessage failed:', err.code, err.message, err);
    }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = !!text.trim() && !!jobId && !!otherUserId && !!currentUser?.uid && !sending;

  return (
    <div className="job-chat">
      <div className="jc-messages">
        {messages.length === 0 && (
          <p className="jc-empty">No messages yet — say hello to get started!</p>
        )}
        {messages.map(msg => {
          const isMe = msg.senderId === currentUser?.uid;
          return (
            <div key={msg.id} className={`jc-msg ${isMe ? 'jc-msg-me' : 'jc-msg-them'}`}>
              {!isMe && <div className="jc-sender">{msg.senderName}</div>}
              <div className="jc-bubble">{msg.text}</div>
              <div className="jc-time">{formatTime(msg.createdAt)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="jc-input-row">
        <input
          className="jc-input"
          type="text"
          placeholder={otherUserId ? 'Type a message...' : 'Chat unavailable'}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={500}
          disabled={!otherUserId}
        />
        <button
          className="jc-send-btn"
          onClick={handleSend}
          disabled={!canSend}
        >
          {sending ? '...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default JobChat;
